'use client'
import React, { useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import Button from 'ui/components/Button'
import { AddressValues, useStore } from 'web/src/lib/context/store-context'
import { CheckoutFormSchema } from 'ui/components/CheckoutForm'
import { useFormContext } from 'react-hook-form'
import routes from 'web/src/lib/routes'
import getShippingData from 'ui/utils/getShippingData'
import { Cart } from '@medusajs/medusa'
import { trackAddPaymentInfo } from 'web/src/utils/tracking/trackAddPaymentInfo'

const PaymentElements = ({ cart }: { cart: Cart }) => {
    const stripe = useStripe()
    const elements = useElements()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { setAddresses } = useStore()
    const [selectedPaymentType, setSelectedPaymentType] = useState<null | string>(null)

    const { handleSubmit } = useFormContext<CheckoutFormSchema>()

    const handlePayment = async (updatedCart?: Omit<Cart, 'refundable_amount' | 'refunded_total'>) => {
        if (!stripe || !elements || !updatedCart || !updatedCart?.id) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            setError('Er is een fout opgetreden, probeer het opnieuw')
            setIsLoading(false)
            return
        }
        if (cart?.total) {
            if (selectedPaymentType) {
                trackAddPaymentInfo(cart?.items, selectedPaymentType, cart.total / 100)
            }
        }

        // Required
        await elements.submit()

        await stripe
            .confirmPayment({
                elements,
                clientSecret: `${updatedCart.payment_session!.data.client_secret}`,
                confirmParams: {
                    return_url: `${process.env.NEXT_PUBLIC_SITE_URL}/${routes.checkPayment}?cart_id=${updatedCart.id}`,
                    payment_method_data: {
                        billing_details: {
                            address: {
                                city: updatedCart?.billing_address?.city!,
                                country: updatedCart?.billing_address?.country_code!,
                                line1: `${updatedCart?.billing_address?.address_1!} ${updatedCart?.billing_address
                                    ?.address_2!}`,
                                postal_code: updatedCart?.billing_address?.postal_code!,
                                state: '',
                                line2: '',
                            },
                            email: updatedCart?.email,
                            name: `${updatedCart?.billing_address?.first_name} ${updatedCart?.billing_address?.last_name}`,
                            phone: updatedCart?.billing_address?.phone!,
                        },
                    },
                },
            })
            .then(function (result) {
                setIsLoading(false)
                if (result.error) {
                    setError('Er is een fout opgetreden, probeer het opnieuw')
                }
            })
    }

    const onSubmit = async (data: CheckoutFormSchema) => {
        const shippingAddress = getShippingData(data.shipping_address) as AddressValues
        const shippingAddress2 = shippingAddress.address_2.concat(' ').concat(shippingAddress.addition || '')
        shippingAddress.address_2 = shippingAddress2
        delete shippingAddress.addition

        setIsLoading(true)

        const cart = await setAddresses({
            email: data.email,
            shipping_address: shippingAddress as AddressValues,
            billing_address: shippingAddress as AddressValues,
            context: {
                dealer: data.dealer,
                license_plate: data.licence_plate,
                newsletter: Boolean(data?.newsletter),
                salutation: data.salutation,
            },
        })

        await handlePayment(cart)
    }

    return (
        <>
            {error && <div className="text-brand-red">{error}</div>}
            <PaymentElement
                onChange={event => {
                    if (event.elementType === 'payment') {
                        setSelectedPaymentType(event.value.type)
                    }
                }}
                options={{
                    layout: {
                        type: 'accordion',
                        defaultCollapsed: false,
                        radios: true,
                        spacedAccordionItems: true,
                    },
                    fields: {
                        billingDetails: {
                            name: 'never',
                            email: 'never',
                            phone: 'never',
                            address: 'never',
                        },
                    },
                }}
            />
            <Button
                as={'button'}
                onClick={handleSubmit(onSubmit)}
                aria-label={'Veilig afrekenen'}
                loading={isLoading}
                variant="primary"
                disabled={!stripe}
            >
                Veilig afrekenen
            </Button>
        </>
    )
}

export default PaymentElements
